/**
 * `/admin/messages` — where the notice board is written.
 *
 * Grouped by *placement*, not by kind, because placement is the thing this
 * screen changes. An admin arrives asking "what is on the home page right now",
 * and the answer is the first section. The employee-facing page groups by kind
 * instead, which is the question a reader has.
 *
 * `HIDDEN` gets its own section at the bottom, labelled as inactive and stated
 * as such: a hidden notice is invisible everywhere else in the product, so this
 * is the only place it can be found again, un-hidden, or deleted. Drafts are
 * left in their placement section rather than pulled into a fourth — a draft
 * still says where it *will* appear, and splitting on two axes at once makes a
 * short list hard to read.
 *
 * Ranking is a number field rather than drag-and-drop. The design system has no
 * drag affordance (no sortable list, no drag handle), and hand-rolling one
 * would mean hand-rolling the keyboard and screen-reader story that goes with
 * it. A number an admin types is worse to use and better than a reordering
 * nobody can operate without a mouse.
 */

import { Icon, Plus } from "@norican/icons";
import { Button, Typography } from "@norican/react";
import { useMemo, useState } from "react";

import {
  AppBreadcrumbs,
  EmptyStateBlock,
  ErrorState,
  LoadingRows,
  PageContainer,
} from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";
import type { Message, MessagePlacement } from "@/lib/api";

import { AdminMessageRow } from "./admin-message-row";
import { useAdminMessages } from "./api";
import { MessageFormDialog } from "./message-form-dialog";
import { MessageHistoryDialog } from "./message-history-dialog";

/** Section order on this page: most visible first, inactive last. */
const SECTION_ORDER: readonly MessagePlacement[] = ["HOME", "CENTER", "HIDDEN"];

/** Rank first, then newest — the same order the public list is served in. */
function byRankThenNewest(a: Message, b: Message): number {
  return (a.rank ?? 0) - (b.rank ?? 0) || b.created_at.localeCompare(a.created_at);
}

export function AdminMessagesPage() {
  const { t } = useT("messages");
  useDocumentTitle(t("admin.documentTitle"));

  const query = useAdminMessages();

  const [editing, setEditing] = useState<Message | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [historyId, setHistoryId] = useState<string | null>(null);

  const sections = useMemo(() => {
    const messages = query.data ?? [];
    return SECTION_ORDER.map((placement) => ({
      placement,
      items: messages.filter((message) => message.placement === placement).sort(byRankThenNewest),
    }));
  }, [query.data]);

  const isEmpty = sections.every((section) => section.items.length === 0);

  function openCreate() {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(message: Message) {
    setEditing(message);
    setFormOpen(true);
  }

  return (
    <PageContainer
      title={t("admin.title")}
      subtitle={t("admin.subtitle")}
      breadcrumbs={<AppBreadcrumbs items={[{ label: t("admin.title") }]} />}
      width="wide"
      actions={
        <Button startIcon={<Icon icon={Plus} size="sm" />} onClick={openCreate}>
          {t("admin.new")}
        </Button>
      }
    >
      {query.isLoading ? (
        <LoadingRows rows={4} />
      ) : query.error != null ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : isEmpty ? (
        <EmptyStateBlock
          title={t("admin.empty.title")}
          description={t("admin.empty.hint")}
          actions={<Button onClick={openCreate}>{t("admin.new")}</Button>}
        />
      ) : (
        sections.map((section) => (
          <section key={section.placement} className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <Typography as="h2" variant="heading-4">
                {t(`admin.section.${section.placement}` as "admin.section.HOME")}
              </Typography>
              <Typography variant="body-sm" tone="muted">
                {t(`admin.sectionHint.${section.placement}` as "admin.sectionHint.HOME")}
              </Typography>
            </div>

            {section.items.length === 0 ? (
              <EmptyStateBlock title={t("admin.sectionEmpty")} description={null} size="sm" />
            ) : (
              section.items.map((message) => (
                <AdminMessageRow
                  key={message.message_id}
                  message={message}
                  onEdit={openEdit}
                  onShowHistory={setHistoryId}
                />
              ))
            )}
          </section>
        ))
      )}

      <MessageFormDialog open={formOpen} onOpenChange={setFormOpen} message={editing} />
      <MessageHistoryDialog
        messageId={historyId}
        open={historyId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setHistoryId(null);
          }
        }}
      />
    </PageContainer>
  );
}
